import React from 'react'
import { observer } from 'mobx-react-lite'

import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'

import {
  Loader,
  useAppConfig,
  useCategoryStore,
  useGlobalCopyStore,
  useGlobalUiStore,
  useScreenSize,
  useWidgetLoadTimer,
  WidgetContainer,
} from '../common'
import { WidgetProps } from '../core'

import { SettingsWidgetContents } from './components/SettingsWidgetContents'

export enum SettingsTabs {
  Profile = 0,
  Devices = 1,
  Notifications = 2,
  TransactionRules = 3,
  Help = 4,
}

export const SettingsWidget = observer(({ onBackClick, onMenuClick, sx }: WidgetProps) => {
  const { settings: copy } = useGlobalCopyStore()
  const { isCopyLoaded, isInitialized } = useGlobalUiStore()
  const { config } = useAppConfig()
  const { isMobile } = useScreenSize()
  const { categoriesLoaded, loadCategories } = useCategoryStore()

  useWidgetLoadTimer({
    widgetName: 'SettingsWidget',
    isLoaded: isInitialized && isCopyLoaded,
  })

  React.useEffect(() => {
    if (!categoriesLoaded) {
      loadCategories().finally()
    }
  }, [])

  const {
    display_user_profile_in_settings: userProfileEnabled,
    display_mobile_devices_in_settings: mobileDevicesEnabled,
    display_notification_settings_in_settings: notificationsEnabled,
    display_user_transaction_rules_in_settings: transactionRulesEnabled,
    show_help_widget_in_master: helpEnabled,
  } = config

  const tabs = [
    { enabled: userProfileEnabled, index: 0, label: copy?.profile_label },
    { enabled: mobileDevicesEnabled, index: 1, label: copy?.mobile_devices_label },
    { enabled: notificationsEnabled, index: 2, label: copy?.notifications_label },
    { enabled: transactionRulesEnabled, index: 3, label: copy?.transaction_rules_label },
    { enabled: helpEnabled, index: 4, label: copy?.help_label },
  ]

  const firstEnabledTab = tabs.find((tab) => tab.enabled)
  const [selectedTab, setSelectedTab] = React.useState(firstEnabledTab ? firstEnabledTab.index : -1)

  if (!isCopyLoaded || !isInitialized) {
    return <Loader />
  }

  return (
    <WidgetContainer
      onBackClick={onBackClick}
      onMenuClick={onMenuClick}
      showAccountFilter={false}
      sx={{ height: 'calc(100dvh - 24px)', overflow: 'auto', ...sx }}
      title={copy.title}
    >
      <Stack mx={isMobile ? 0 : 48} my={24}>
        <Tabs
          onChange={(_, newValue) => setSelectedTab(newValue)}
          sx={{ bgcolor: 'background.paper' }}
          textColor="primary"
          value={selectedTab}
          variant="scrollable"
        >
          {tabs.map(
            (tab) => tab.enabled && <Tab key={tab.index} label={tab.label} value={tab.index} />,
          )}
        </Tabs>

        <SettingsWidgetContents selectedTab={selectedTab} />
      </Stack>
    </WidgetContainer>
  )
})
